/** @jsx jsx */
import { Box, FileEntry, FileEntryProps, Flex, FocusRing } from "@eworkit/iris";
import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { FilePreviewModal } from "@/components/Files/preview/FilePreviewModal";
import { isMobile } from "@/core/device/service";
import { AttachmentLike, getAttachmentMeta } from "@/data/attachments";
import { downloader } from "@/eworkitComponents";
import { useToasts } from "@/views/components/Toast/useToasts";

export interface PreviewFileEntryProps
  extends Omit<FileEntryProps, "ariaLabelCancel" | "ariaLabelDelete" | "ariaLabelDownload" | "ariaLabelRemove"> {
  attachment: AttachmentLike;
}

export const PreviewFileEntry: FC<PreviewFileEntryProps> = ({ attachment, ...fileEntryProps }) => {
  const { t } = useTranslation(["public", "common"]);

  const { addToast } = useToasts();

  const modalState = useDialogState({ animated: true });

  const showPreview = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      modalState.show();
    },
    [modalState],
  );

  const download = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        const meta = await getAttachmentMeta({ systemId: attachment.systemId });
        downloader(meta.publicUrl);
      } catch (error) {
        addToast({
          text: t("common:ERROR_MESSAGES.GENERAL"),
          type: "danger",
        });
      }
    },
    [attachment.systemId, addToast, t],
  );

  return (
    <React.Fragment>
      <Flex>
        <Box sx={{ maxWidth: "100%" }} flexGrow={1}>
          <FocusRing>
            <FileEntry
              as="button"
              ariaLabelCancel={t("COMPONENTS.FILE_UPLOAD.CANCEL")}
              ariaLabelDelete={t("COMPONENTS.FILE_UPLOAD.DELETE")}
              ariaLabelRemove={t("COMPONENTS.FILE_UPLOAD.DELETE")}
              ariaLabelDownload={t("COMPONENTS.FILE_UPLOAD.DOWNLOAD")}
              file={attachment}
              onClick={isMobile ? download : showPreview}
              {...fileEntryProps}
            />
          </FocusRing>
        </Box>
      </Flex>

      {modalState.visible && (
        <FilePreviewModal attachment={attachment} modalState={modalState} onDownloadButtonClick={download} />
      )}
    </React.Fragment>
  );
};
